import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PendingTestimonial } from '@mys/contracts';
import { api, ApiError } from '../lib/api.js';

/* ============================================================================
   /testimonials — the approval queue.
   ----------------------------------------------------------------------------
   Nothing here is live on the public site until someone presses approve. The
   light edit box is for typos and line breaks, not for changing what a client
   said.
   ========================================================================== */

interface PendingResponse {
  ok: true;
  items: PendingTestimonial[];
}

function Row({ item }: { item: PendingTestimonial }) {
  const client = useQueryClient();
  const [quote, setQuote] = useState(item.quote);

  const review = useMutation({
    mutationFn: (approved: boolean) =>
      api.patch(`/api/testimonials/${item.id}`, {
        approved,
        // Only send an edit when there actually is one.
        ...(quote.trim() !== item.quote ? { quote: quote.trim() } : {}),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['testimonials', 'pending'] }),
  });

  return (
    <li className="card stack" style={{ listStyle: 'none' }}>
      <div className="muted">
        {item.business ?? 'no client name'} · {'\u2605'.repeat(item.rating)}
      </div>

      <div className="field">
        <label htmlFor={`quote-${item.id}`} className="sr-only">
          Quote
        </label>
        <textarea
          id={`quote-${item.id}`}
          value={quote}
          maxLength={600}
          onChange={(event) => setQuote(event.target.value)}
        />
      </div>

      <div className="row">
        <button
          type="button"
          className="btn btn--small"
          disabled={review.isPending}
          onClick={() => review.mutate(true)}
        >
          Approve
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={review.isPending}
          onClick={() => review.mutate(false)}
        >
          Abhi nahi
        </button>
        {review.isError ? (
          <span className="field__error" role="alert">
            {review.error instanceof ApiError ? review.error.message : 'Could not save.'}
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function TestimonialsPage() {
  const { data, isPending, error } = useQuery({
    queryKey: ['testimonials', 'pending'],
    queryFn: ({ signal }) => api.get<PendingResponse>('/api/testimonials/pending', signal),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Testimonials</h1>
          <p className="muted">Nothing appears on the site until it is approved.</p>
        </div>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the queue.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && data.items.length === 0 ? (
        <p className="muted">The queue is empty.</p>
      ) : (
        <ul className="stack" style={{ padding: 0, margin: 0 }}>
          {data?.items.map((item) => (
            <Row key={item.id} item={item} />
          ))}
        </ul>
      )}
    </>
  );
}
